import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import CreateCar from '../Create/CreateCar'
import CreateTransport from '../Create/CreateTransport'

import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import type { CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'

interface Props {
  tripId: string
  defaultDate?: string
  sectionId?: string
  rankSpec?: CreateTripItemRankSpec
}

function SelectTransportType(props: Props) {
  const { tripId, defaultDate, rankSpec } = props

  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const handleAddCar = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_transport_car_rental'))
    openModal(CreateCar, { tripId, defaultDate, rankSpec })
  }, [openModal, tripId, defaultDate, rankSpec])

  const handleAddTransport = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_transport_public'))
    openModal(CreateTransport, {
      tripId,
      defaultDate,
      rankSpec,
    })
  }, [openModal, tripId, defaultDate, rankSpec])

  const carRentalTitle = intl.formatMessage({
    id: 'pmntgwl1yt1e2uYfLgMD',
    defaultMessage: 'Add a car rental',
    description: 'List item title for adding a car rental in trip planner transport selection',
  })
  const carRentalSubtitle = intl.formatMessage({
    id: 'gqjHoQwIqVhYzpcbPsTq',
    defaultMessage: 'Renting private vehicle',
    description: 'List item subtitle for car rental option in trip planner transport selection',
  })
  const addTransportTitle = intl.formatMessage({
    id: 'aA20NT7Ipb0--7hq-nrN',
    defaultMessage: 'Add transport',
    description: 'List item title for adding public transport in trip planner',
  })
  const addTransportSubtitle = intl.formatMessage({
    id: '00L5d7PtAs9YII_y4yo_',
    defaultMessage: 'Rail, bus, ferry',
    description: 'List item subtitle listing public transport types in trip planner',
  })

  const listItemsData = useMemo(
    () => [
      {
        startIcon: <LineCarIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'addCarRental',
        title: carRentalTitle,
        subtitle: carRentalSubtitle,
        onClick: handleAddCar,
      },
      {
        startIcon: <LineBusIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'addTransport',
        title: addTransportTitle,
        subtitle: addTransportSubtitle,
        onClick: handleAddTransport,
      },
    ],
    [handleAddTransport, handleAddCar, carRentalTitle, carRentalSubtitle, addTransportTitle, addTransportSubtitle],
  )

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'ZBgpQgZk-gJMRA76ppfN',
          defaultMessage: 'Transport',
          description: 'Modal title for transport type selection in trip planner',
        })}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {listItemsData.map((item) => (
            <ClickableListItem
              key={item.key}
              startIcon={item.startIcon}
              endIcon={item.endIcon}
              title={item.title}
              subtitle={item.subtitle}
              onClick={item.onClick}
            />
          ))}
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SelectTransportType
